import React, { useState } from 'react';
import { mediaUrl } from '@/lib/api';
import {
  Users, BedDouble, Ruler, Eye, ImageIcon, Coffee, ShieldCheck,
  Wallet, CreditCard, Globe, Star, Tv, Wind, Bath, Sparkles, MapPin, Clock, Compass,
  Grid3x3, X, ChevronLeft, ChevronRight, Wifi, PawPrint,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CATEGORY_ORDER, AMENITY_CATEGORIES } from '@/constants/hotelAmenities';
import { amenityIcon } from '@/constants/hotelAmenityIcons';
import { RoomBookingBox } from '@/components/hotel/RoomBookingBox';

const CAT_ICON = { Popolari: Star, Elettronica: Tv, Elettrodomestici: Coffee, Ventilazione: Wind, Servizi: Bath, Caratteristiche: Eye, Altri: Sparkles };

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const bedsText = (beds) => (Array.isArray(beds) && beds.length
  ? beds.map((b) => `${b.count || b.quantity || 1} ${b.type || 'letto'}`).join(' · ') : '');

// Inferisce la categoria di un servizio (per le amenity salvate come semplice stringa).
function inferCategory(name) {
  const n = String(name || '').toLowerCase();
  for (const cat of AMENITY_CATEGORIES) {
    for (const item of cat.items) {
      const it = item.toLowerCase();
      if (n === it || n.includes(it) || it.includes(n)) return cat.key;
    }
  }
  return 'Altri';
}

// Normalizza un servizio (stringa o oggetto {name, category, paid}) a forma canonica.
function normalizeAmenity(a) {
  if (!a) return null;
  if (typeof a === 'string') {
    const name = a.trim();
    return name ? { name, category: inferCategory(name), paid: false } : null;
  }
  const name = (a.name || '').trim();
  if (!name) return null;
  const category = a.category && CATEGORY_ORDER.includes(a.category) ? a.category : inferCategory(name);
  return { name, category, paid: !!a.paid };
}

// Span delle foto secondarie per riempire sempre la metà destra senza buchi.
function rightSpan(count, i) {
  if (count === 1) return 'col-span-2 row-span-2';
  if (count === 2) return 'col-span-2 row-span-1';
  if (count === 3) return i === 0 ? 'col-span-2 row-span-1' : 'col-span-1 row-span-1';
  return 'col-span-1 row-span-1';
}

/**
 * Vista pubblica della camera — presentazionale, riusabile.
 * Galleria a mosaico stile Airbnb + servizi con icone. Nessun fetch qui dentro.
 */
export function RoomPublicView({ room, price, publicMode = false, bookHref = '/contatti' }) {
  const images = [room?.main_image, ...((room?.images) || [])].filter(Boolean);
  const [lightbox, setLightbox] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const openLightbox = (i = 0) => { setActiveIdx(i); setLightbox(true); };
  const closeLightbox = () => setLightbox(false);
  const prev = (e) => { e?.stopPropagation?.(); setActiveIdx((v) => (v - 1 + images.length) % images.length); };
  const next = (e) => { e?.stopPropagation?.(); setActiveIdx((v) => (v + 1) % images.length); };

  const hero = images[0];
  const rights = images.slice(1, 5);

  // Servizi: normalizza (stringa o oggetto), scarta le voci senza nome, raggruppa per categoria.
  const validAmenities = (room?.amenities || []).map(normalizeAmenity).filter(Boolean);
  const grouped = {};
  validAmenities.forEach((a) => {
    const k = CATEGORY_ORDER.includes(a.category) ? a.category : 'Altri';
    (grouped[k] = grouped[k] || []).push(a);
  });
  const visibleCategories = CATEGORY_ORDER.filter((k) => grouped[k]?.length);

  const typeLabel = room?.type_custom || room?.type_standard || room?.category || 'Camera';

  const _allBeds = (room?.bedrooms?.length ? room.bedrooms.flatMap((r) => r.beds || []) : room?.beds) || [];
  const _bedCount = _allBeds.reduce((s, b) => s + (Number(b.quantity) || 0), 0);
  const meta = [
    room?.capacity_total ? { icon: Users, label: `${room.capacity_total} ospiti` } : null,
    (room?.bedrooms?.length) ? { icon: BedDouble, label: `${room.bedrooms.length} camer${room.bedrooms.length === 1 ? 'a' : 'e'} da letto` } : null,
    _bedCount ? { icon: BedDouble, label: `${_bedCount} letti` } : null,
    room?.area_sqm ? { icon: Ruler, label: `${room.area_sqm} m²` } : null,
    room?.view && room.view !== 'Nessuna' ? { icon: Eye, label: `Vista ${room.view}` } : null,
  ].filter(Boolean);

  const policies = [
    room?.breakfast_included ? { icon: Coffee, label: 'Colazione inclusa' } : null,
    room?.free_cancellation ? { icon: ShieldCheck, label: 'Cancellazione gratuita' } : null,
    (room?.pay_at_property && !publicMode) ? { icon: Wallet, label: 'Pagamento in struttura' } : null,
  ].filter(Boolean);

  return (
    <div className="bg-background" data-testid="room-public-view">
      {/* GALLERIA — mosaico stile Airbnb */}
      {images.length === 0 ? (
        <div className="flex h-[260px] w-full items-center justify-center rounded-2xl bg-muted sm:h-[440px]" data-testid="room-gallery-empty">
          <ImageIcon className="h-10 w-10 text-muted-foreground" />
        </div>
      ) : images.length === 1 ? (
        <button
          type="button"
          onClick={() => openLightbox(0)}
          className="relative block h-[260px] w-full overflow-hidden rounded-2xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-[440px]"
          data-testid="room-gallery-single"
        >
          <img src={mediaUrl(hero)} alt={room?.name} className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.02]" />
        </button>
      ) : (
        <div className="relative">
          {/* Mobile: hero singolo */}
          <button
            type="button"
            onClick={() => openLightbox(0)}
            className="relative block h-[260px] w-full overflow-hidden rounded-2xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
            data-testid="room-gallery-mobile"
          >
            <img src={mediaUrl(hero)} alt={room?.name} className="h-full w-full object-cover" />
          </button>

          {/* Desktop: mosaico 1 grande + fino a 4 */}
          <div className="hidden h-[440px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-2xl sm:grid" data-testid="room-gallery-mosaic">
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="group col-span-2 row-span-2 overflow-hidden bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <img src={mediaUrl(hero)} alt={room?.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
            </button>
            {rights.map((u, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openLightbox(i + 1)}
                className={cn('group overflow-hidden bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', rightSpan(rights.length, i))}
              >
                <img src={mediaUrl(u)} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
              </button>
            ))}
          </div>

          {/* Bottone "Mostra tutte le foto" */}
          <button
            type="button"
            onClick={() => openLightbox(0)}
            className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg border border-border bg-background/95 px-3 py-1.5 text-sm font-semibold text-foreground shadow-sm backdrop-blur transition-colors hover:bg-background"
            data-testid="room-gallery-show-all"
          >
            <Grid3x3 className="h-4 w-4" />Mostra tutte le foto ({images.length})
          </button>
        </div>
      )}

      {/* HEADER + PREZZO */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">{typeLabel}</span>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground">{room?.name || 'Camera'}</h1>
          {room?.hotel?.name && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="room-hotel-info">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-medium text-foreground">{room.hotel.name}</span>
              {room.hotel.address && <span>· {room.hotel.address}</span>}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-4">
            {meta.map((m, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm text-muted-foreground"><m.icon className="h-4 w-4 text-primary" />{m.label}</span>
            ))}
          </div>
          {room?.description && <p className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{room.description}</p>}
          {room?.description_extra && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{room.description_extra}</p>}

          {(room?.bedrooms?.length > 0) && (
            <div className="mt-6" data-testid="room-bedrooms">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight text-foreground"><BedDouble className="h-5 w-5 text-primary" />Camere da letto</h2>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {room.bedrooms.map((b, i) => (
                  <div key={b.id || i} className="rounded-2xl border border-border bg-card p-4" data-testid={`room-bedroom-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">{b.name || `Camera ${i + 1}`}</p>
                      {b.area_sqm ? <span className="text-xs text-muted-foreground">{b.area_sqm} m²</span> : null}
                    </div>
                    {(b.beds || []).length > 0 && (
                      <p className="mt-1 text-sm text-muted-foreground">{bedsText(b.beds)}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(b.bathroom === 'Privato' || (b.bathroom == null && b.private_bathroom)) && <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground"><Bath className="h-3 w-3" />Bagno privato</span>}
                      {b.bathroom === 'In comune' && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"><Bath className="h-3 w-3" />Bagno in comune</span>}
                      {b.air_conditioning && <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">Aria condizionata</span>}
                      {b.balcony && <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">Balcone</span>}
                      {b.extra_bed && <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">Letto aggiuntivo</span>}
                    </div>
                    {b.notes && <p className="mt-2 text-xs leading-relaxed text-foreground/80">{b.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(room?.wifi || room?.pets_allowed || room?.total_bathrooms > 0 || room?.deposit > 0) && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="room-unit-details">
              {room?.total_bathrooms > 0 && <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium"><Bath className="h-3.5 w-3.5 text-primary" />{room.total_bathrooms} bagn{room.total_bathrooms === 1 ? 'o' : 'i'}</span>}
              {room?.wifi && <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium"><Wifi className="h-3.5 w-3.5 text-primary" />Wi-Fi</span>}
              {room?.pets_allowed && <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium"><PawPrint className="h-3.5 w-3.5 text-primary" />Animali ammessi</span>}
              {room?.deposit > 0 && <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium">Deposito {euro(room.deposit)}</span>}
            </div>
          )}
        </div>

        {/* Box prezzo */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-end gap-1">
              <span className="font-display text-3xl font-bold text-foreground" data-testid="room-public-price">{euro(room?.base_price)}</span>
              <span className="mb-1 text-sm text-muted-foreground">/ {room?.pricing_unit || 'notte'}</span>
            </div>
            {price?.total != null && (
              <p className="mt-1 text-sm text-muted-foreground">Totale stimato 7 notti: <b className="text-foreground">{euro(price.total)}</b></p>
            )}
            {(room?.hotel?.checkin_time || room?.hotel?.checkout_time) && (
              <div className="mt-4 flex items-center gap-4 border-t border-border pt-4 text-sm text-foreground" data-testid="room-checkin-times">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />Check-in {room.hotel.checkin_time || '—'}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />Check-out {room.hotel.checkout_time || '—'}</span>
              </div>
            )}
            {policies.length > 0 && (
              <div className="mt-4 space-y-2 border-t border-border pt-4">
                {policies.map((p, i) => (
                  <p key={i} className="flex items-center gap-2 text-sm text-foreground"><p.icon className="h-4 w-4 text-primary" />{p.label}</p>
                ))}
              </div>
            )}
            {publicMode ? (
              <RoomBookingBox room={room} />
            ) : (
              <>
                <button type="button" disabled className="mt-4 w-full cursor-not-allowed rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground opacity-90">
                  Prenota ora
                </button>
                <p className="mt-2 text-center text-[11px] text-muted-foreground">Anteprima — nessuna prenotazione reale</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* SERVIZI RAGGRUPPATI — icone reali, solo categorie presenti */}
      {visibleCategories.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-bold tracking-tight text-foreground">Servizi</h2>
          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            {visibleCategories.map((k) => {
              const CatIcon = CAT_ICON[k] || Sparkles;
              return (
                <div key={k} className="rounded-2xl border border-border bg-card p-4" data-testid={`amenity-category-${k}`}>
                  <div className="mb-3 flex items-center gap-2">
                    <CatIcon className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">{k}</h3>
                  </div>
                  <ul className="space-y-2">
                    {grouped[k].map((a, idx) => {
                      const Icon = amenityIcon(a.name);
                      return (
                        <li key={`${a.name}-${idx}`} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2.5 text-foreground">
                            <Icon className="h-[18px] w-[18px] shrink-0 text-primary" />{a.name}
                          </span>
                          {a.paid && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">A pagamento</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* POLICY / INFO */}
      {(room?.policy_text || (room?.accepted_payments || []).length > 0 || (room?.languages || []).length > 0) && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-bold tracking-tight text-foreground">Condizioni</h2>
          {room?.policy_text && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{room.policy_text}</p>}
          <div className="mt-4 flex flex-wrap gap-6">
            {(room?.accepted_payments || []).length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><CreditCard className="h-3.5 w-3.5" />Pagamenti</p>
                <div className="flex flex-wrap gap-1.5">{room.accepted_payments.map((p) => <span key={p} className="rounded-full bg-muted px-2.5 py-1 text-xs">{p}</span>)}</div>
              </div>
            )}
            {(room?.languages || []).length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Globe className="h-3.5 w-3.5" />Lingue</p>
                <div className="flex flex-wrap gap-1.5">{room.languages.map((l) => <span key={l} className="rounded-full bg-muted px-2.5 py-1 text-xs">{l}</span>)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NEI DINTORNI (concierge) */}
      {(room?.concierge?.points_of_interest || []).length > 0 && (
        <div className="mt-8" data-testid="room-concierge">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight text-foreground">
            <Compass className="h-5 w-5 text-primary" />Nei dintorni
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Punti di interesse selezionati dalla struttura.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {room.concierge.points_of_interest.map((p, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-2xl border border-border bg-card p-4" data-testid={`poi-${i}`}>
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{p.name}</p>
                  {p.category && <p className="text-xs text-muted-foreground">{p.category}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LIGHTBOX — visualizzatore a schermo intero */}
      {lightbox && images.length > 0 && (
        <div
          className="fixed inset-0 z-[200] flex flex-col bg-black/95"
          onClick={closeLightbox}
          data-testid="room-gallery-lightbox"
        >
          <div className="flex items-center justify-between p-4 text-white">
            <span className="text-sm font-medium">{activeIdx + 1} / {images.length}</span>
            <button type="button" onClick={closeLightbox} className="rounded-full p-2 transition-colors hover:bg-white/10" data-testid="lightbox-close" aria-label="Chiudi">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-4 pb-6" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && (
              <button type="button" onClick={prev} className="absolute left-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20" aria-label="Precedente">
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            <img src={mediaUrl(images[activeIdx])} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
            {images.length > 1 && (
              <button type="button" onClick={next} className="absolute right-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20" aria-label="Successiva">
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomPublicView;
